import TrashCan16 from '@carbon/icons-react/es/trash-can/16';
import { Tooltip } from 'antd';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Menu } from 'components/atoms/dropdown';
import { confirm } from 'components/atoms/modal';
import { PlaceholderAvatar } from 'components/molecules/placeholder-avatar';
import { TeamAsLeafResource } from 'components/organisms/resource-management/resource-types';
import { CellWrapper } from 'components/organisms/resource-management/scheduler-config/columns/cells/component';
import { NameCell } from 'components/organisms/timesheets/common/cells/name-cell';
import { useTranslate } from 'hooks/use-translation';

type Props = Pick<TeamAsLeafResource, 'teamId' | 'teamName'> & {
  onRemove: () => void;
  onEdit: () => void;
  taskAssignedTeamIds: number[];
  readOnly: boolean;
};

export const TeamCell: FC<Props> = ({
  teamName,
  teamId,
  onRemove,
  taskAssignedTeamIds,
  readOnly,
}) => {
  const translate = useTranslate();
  const removeTeam = useCallback(() => {
    const userRemovalConfirmationTexts = {
      title: translate(
        'resource_management.actions.remove_member.confirmation.title',
        {
          memberType: translate('placeholder').toLowerCase(),
        },
      ),
      content: translate(
        'resource_management.actions.remove_member.confirmation.message.finisher',
      ),
    };

    confirm({
      ...userRemovalConfirmationTexts,
      okText: translate('remove'),
      okType: 'danger',
      onOk: onRemove,
    });
  }, [onRemove, translate]);

  return (
    <>
      <CellWrapper
        menu={
          readOnly === true ? undefined : (
            <Menu>
              <Menu.Header>
                <span>
                  <FormattedMessage id="more_actions" />
                </span>
              </Menu.Header>
              <Menu.ItemDelete
                disabled={taskAssignedTeamIds.includes(teamId)}
                icon={<TrashCan16 />}
                onClick={removeTeam}
              >
                <Tooltip
                  title={
                    taskAssignedTeamIds.includes(teamId) ? (
                      <FormattedMessage id="templates.resource_management.remove_team.disabled.message" />
                    ) : null
                  }
                >
                  <span>
                    <FormattedMessage id="resource_management.actions.remove_placeholder_from_template" />
                  </span>
                </Tooltip>
              </Menu.ItemDelete>
            </Menu>
          )
        }
      >
        <NameCell
          logo={<PlaceholderAvatar isCustomerRole={false} name={teamName} />}
          primaryText={teamName}
        />
      </CellWrapper>
    </>
  );
};
